/**
 * Cinematic Overlay - Creates immersive decision moments
 *
 * Effects:
 * - Vignette for tunneling attention
 * - Scene dimming during decisions
 * - Spotlight on conflict area
 * - Smooth transitions
 */

import { Container, Graphics } from 'pixi.js';
import { theme } from '@core/theme';

export interface CinematicConfig {
  width: number;
  height: number;
}

export class CinematicOverlay extends Container {
  private overlayWidth: number;
  private overlayHeight: number;

  // Layers
  private vignetteLayer: Graphics;
  private dimLayer: Graphics;
  private spotlightLayer: Graphics;

  // State
  private isDecisionMode = false;
  private currentDimAlpha = 0;
  private targetDimAlpha = 0;
  private animationFrame: number | null = null;

  constructor(config: CinematicConfig) {
    super();

    this.overlayWidth = config.width;
    this.overlayHeight = config.height;

    // Create layers in order
    this.vignetteLayer = new Graphics();
    this.dimLayer = new Graphics();
    this.spotlightLayer = new Graphics();

    this.addChild(this.vignetteLayer);
    this.addChild(this.dimLayer);
    this.addChild(this.spotlightLayer);

    // Draw initial vignette
    this.drawVignette();

    // Start with dim layer invisible
    this.dimLayer.alpha = 0;
    this.spotlightLayer.alpha = 0;

    // Vignette is always subtly visible
    this.vignetteLayer.alpha = 0.2;
  }

  /**
   * Draw the vignette effect - simple edge darkening
   */
  private drawVignette(): void {
    this.vignetteLayer.clear();

    // Simple 4-edge vignette (much lighter than radial gradient)
    const edgeSize = 80;
    const alpha = 0.15;

    // Top edge
    this.vignetteLayer.rect(0, 0, this.overlayWidth, edgeSize);
    this.vignetteLayer.fill({ color: 0x000000, alpha });

    // Bottom edge
    this.vignetteLayer.rect(0, this.overlayHeight - edgeSize, this.overlayWidth, edgeSize);
    this.vignetteLayer.fill({ color: 0x000000, alpha });

    // Left edge
    this.vignetteLayer.rect(0, edgeSize, edgeSize, this.overlayHeight - edgeSize * 2);
    this.vignetteLayer.fill({ color: 0x000000, alpha: alpha * 0.7 });

    // Right edge
    this.vignetteLayer.rect(this.overlayWidth - edgeSize, edgeSize, edgeSize, this.overlayHeight - edgeSize * 2);
    this.vignetteLayer.fill({ color: 0x000000, alpha: alpha * 0.7 });
  }

  /**
   * Draw scene dimming layer
   */
  private drawDimLayer(): void {
    this.dimLayer.clear();
    this.dimLayer.rect(0, 0, this.overlayWidth, this.overlayHeight);
    this.dimLayer.fill({ color: 0x000000, alpha: 1 });
  }

  /**
   * Enter decision moment - cinematic mode
   */
  enterDecisionMode(_spotlightX?: number, _spotlightY?: number): Promise<void> {
    return new Promise((resolve) => {
      this.isDecisionMode = true;
      this.drawDimLayer();
      this.targetDimAlpha = theme.decisionMoment.sceneDimAlpha;
      this.animateTransition(resolve);
    });
  }

  /**
   * Exit decision moment - return to normal
   */
  exitDecisionMode(): Promise<void> {
    return new Promise((resolve) => {
      this.isDecisionMode = false;
      this.targetDimAlpha = 0;

      this.animateTransition(() => {
        this.spotlightLayer.alpha = 0;
        resolve();
      });
    });
  }

  /**
   * Intensify for consequence - brief moment of high drama
   */
  flashConsequence(severity: 'positive' | 'warning' | 'negative' | 'neutral'): Promise<void> {
    return new Promise((resolve) => {
      const effectKey = severity as keyof typeof theme.consequenceEffects;
      const effect = theme.consequenceEffects[effectKey];
      const targetIntensity = effect.glowIntensity;

      // Quick flash up
      this.animateDimTo(targetIntensity, 150, () => {
        // Hold briefly
        setTimeout(() => {
          // Fade back
          this.animateDimTo(0, effect.duration / 2, resolve);
        }, 200);
      });
    });
  }

  /**
   * Animate the dim layer transition
   */
  private animateTransition(onComplete?: () => void): void {
    const duration = theme.decisionMoment.transitionDuration;
    this.animateDimTo(this.targetDimAlpha, duration, onComplete);
  }

  /**
   * Animate dim alpha to target value
   */
  private animateDimTo(target: number, duration: number, onComplete?: () => void): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    const startTime = performance.now();
    const startAlpha = this.currentDimAlpha;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = theme.easings.easeInOutSine(progress);

      this.currentDimAlpha = startAlpha + (target - startAlpha) * eased;
      this.dimLayer.alpha = this.currentDimAlpha;

      // Also animate vignette intensity
      const vignetteTarget = this.isDecisionMode ? 0.4 : 0.2;
      this.vignetteLayer.alpha = 0.2 + (vignetteTarget - 0.2) * eased;

      // Spotlight follows dim layer
      if (this.isDecisionMode) {
        this.spotlightLayer.alpha = this.currentDimAlpha * 3;
      }

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.currentDimAlpha = target;
        this.dimLayer.alpha = target;
        this.animationFrame = null;
        onComplete?.();
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  /**
   * Set spotlight position (disabled for performance)
   */
  setSpotlight(_x: number, _y: number, _radius = 120): void {
    // Spotlight disabled for performance
  }

  /**
   * Resize the overlay
   */
  resize(width: number, height: number): void {
    this.overlayWidth = width;
    this.overlayHeight = height;
    this.drawVignette();
    this.drawDimLayer();
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    super.destroy();
  }
}
